/**
 * Chat Service — API client for the embedded support chat (respond.io)
 */

import { API_BASE_URL } from '@/constants/config';

const SESSION_KEY = 'partrunner_chat_session';

export interface ChatMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  message_type: 'text' | 'image' | 'file';
  content: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

interface MessagesResponse {
  success: boolean;
  data?: {
    conversationId: string;
    messages: ChatMessage[];
    hasMore: boolean;
  };
}

interface SendResponse {
  success: boolean;
  message: string;
  data?: {
    messageId: string;
    conversationId: string;
  };
}

/**
 * Get or create a stable session-based contact ID for anonymous users.
 */
export function getSessionContactId(): string {
  let sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = `anon_${crypto.randomUUID()}`;
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Build a contact ID for a logged-in flotillero.
 */
export function getUserContactId(flotilleroId: string): string {
  return `user_${flotilleroId}`;
}

/**
 * Fetch conversation history.
 */
export async function fetchMessages(
  contactId: string,
  options?: { limit?: number; before?: string }
): Promise<MessagesResponse> {
  try {
    const params = new URLSearchParams({ contactId });
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.before) params.set('before', options.before);

    const res = await fetch(`${API_BASE_URL}/api/chat/messages?${params}`, {
      credentials: 'include',
    });
    return await res.json();
  } catch {
    return { success: false };
  }
}

/**
 * Send a message from the chat widget.
 */
export async function sendMessage(params: {
  contactId: string;
  text: string;
  contactName?: string;
  contactEmail?: string;
  flotilleroId?: string;
  context?: Record<string, unknown>;
}): Promise<SendResponse> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/chat/send`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    return await res.json();
  } catch {
    return { success: false, message: 'Error de conexión' };
  }
}
